// Career Growth and Financial Assumptions

/**
 * Career growth phases based on age
 */
const CAREER_PHASES = {
  EARLY_CAREER: { ages: [22, 30], baseGrowth: 0.06, promotionChance: 0.25 },    // 6% avg, high promotion chance
  MID_CAREER: { ages: [31, 45], baseGrowth: 0.04, promotionChance: 0.15 },      // 4% avg, moderate promotion
  LATE_CAREER: { ages: [46, 60], baseGrowth: 0.02, promotionChance: 0.08 },     // 2% avg, lower promotion
  PRE_RETIREMENT: { ages: [61, 67], baseGrowth: 0.01, promotionChance: 0.02 }   // 1% avg, rare promotion
};

/**
 * Promotion impact by career phase
 */
const PROMOTION_IMPACT = {
  EARLY_CAREER: 0.15,      // 15% bump
  MID_CAREER: 0.12,        // 12% bump
  LATE_CAREER: 0.08,       // 8% bump
  PRE_RETIREMENT: 0.05     // 5% bump
};

/**
 * Job change impact (typically higher than promotion)
 * Note: Currently unused but kept for future job change feature
 */
// eslint-disable-next-line no-unused-vars
const JOB_CHANGE_IMPACT = {
  EARLY_CAREER: 0.25,      // 25% bump
  MID_CAREER: 0.20,        // 20% bump
  LATE_CAREER: 0.12,       // 12% bump
  PRE_RETIREMENT: 0.08     // 8% bump
};

/**
 * Get career phase for a given age
 */
export const getCareerPhase = (age) => {
  if (age <= 30) return 'EARLY_CAREER';
  if (age <= 45) return 'MID_CAREER';
  if (age <= 60) return 'LATE_CAREER';
  return 'PRE_RETIREMENT';
};

/**
 * Calculate smart career growth rate based on age and experience
 * This models realistic career progression with faster growth early and slower later
 */
export const calculateSmartGrowthRate = (age, yearsAtCurrentIncome = 0, settings = {}) => {
  const {
    useSmartMode = true,
    simpleGrowthRate = 0.03,
    earlyCareerBoost = 1.0,  // Multiplier for early career
    lateCareerPenalty = 1.0  // Multiplier for late career
  } = settings;

  // If simple mode, just return flat rate
  if (!useSmartMode) {
    return simpleGrowthRate;
  }

  const phase = getCareerPhase(age);
  const phaseData = CAREER_PHASES[phase];

  let baseRate = phaseData.baseGrowth;

  // Apply user adjustments
  if (phase === 'EARLY_CAREER') {
    baseRate *= earlyCareerBoost;
  } else if (phase === 'LATE_CAREER' || phase === 'PRE_RETIREMENT') {
    baseRate *= lateCareerPenalty;
  }

  // Stagnation penalty: if at same income level for 3+ years, reduce growth
  if (yearsAtCurrentIncome >= 3) {
    baseRate *= 0.7; // 30% reduction for stagnation
  }

  return Math.max(0, baseRate); // Never negative
};

/**
 * Determine if automatic promotion should occur this year
 * Based on career phase probabilities and time since last promotion
 */
export const shouldAutoPromote = (age, yearsSincePromotion, settings = {}) => {
  const {
    enableAutoPromotions = true,
    promotionFrequency = 1.0  // Multiplier for promotion chance
  } = settings;

  if (!enableAutoPromotions) return false;

  const phase = getCareerPhase(age);
  const phaseData = CAREER_PHASES[phase];

  // Base promotion chance
  let promotionChance = phaseData.promotionChance * promotionFrequency;

  // Higher chance if overdue (5+ years since last promotion)
  if (yearsSincePromotion >= 5) {
    promotionChance *= 1.5;
  } else if (yearsSincePromotion >= 3) {
    promotionChance *= 1.2;
  }

  // Deterministic milestones (guaranteed promotions at key career points)
  const milestoneAges = [25, 30, 35, 40, 45];
  if (milestoneAges.includes(age) && yearsSincePromotion >= 2) {
    return true;
  }

  // Random chance based on calculated probability
  return Math.random() < promotionChance;
};

/**
 * Calculate promotion amount based on current income and career phase
 */
export const calculatePromotionIncome = (currentIncome, age, settings = {}) => {
  const {
    promotionMultiplier = 1.0
  } = settings;

  const phase = getCareerPhase(age);
  const baseImpact = PROMOTION_IMPACT[phase];

  return currentIncome * (1 + baseImpact * promotionMultiplier);
};

/**
 * Default financial assumptions
 */
export const DEFAULT_FINANCIAL_SETTINGS = {
  // Career growth
  useSmartCareerMode: true,
  simpleGrowthRate: 0.03,
  earlyCareerBoost: 1.0,
  lateCareerPenalty: 1.0,
  enableAutoPromotions: false, // Disabled by default to avoid surprises
  promotionFrequency: 1.0,
  promotionMultiplier: 1.0,

  // Economic assumptions
  inflationRate: 0.03,            // 3% annual inflation
  investmentReturn: 0.10,         // 10% nominal return
  realReturn: 0.07,               // 7% real return (after inflation)

  // Retirement
  socialSecurityMultiplier: 1.0,  // % of standard SS benefit
  withdrawalRate: 0.04,           // 4% rule

  // Tax (informational - actual rates in taxCalculations.js)
  federalTaxApproach: 'progressive', // vs 'flat'
  stateTaxRate: 'auto',              // vs custom %

  // 401k
  retirement401kMatchRate: 0.04,  // 4% employer match
  retirement401kMaxMatch: 0.06    // Up to 6% of salary
};

/**
 * Apply inflation to a value
 */
export const applyInflation = (value, years, inflationRate = 0.03) => {
  return value * Math.pow(1 + inflationRate, years);
};

/**
 * Calculate real return from nominal return
 */
export const calculateRealReturn = (nominalReturn, inflationRate = 0.03) => {
  // Fisher equation: (1 + nominal) = (1 + real) * (1 + inflation)
  return (1 + nominalReturn) / (1 + inflationRate) - 1;
};

/**
 * Estimate Social Security benefit (simplified)
 * Based on average indexed monthly earnings (AIME)
 */
export const estimateSocialSecurityBenefit = (avgAnnualIncome, claimAge = 67, settings = {}) => {
  const {
    socialSecurityMultiplier = 1.0
  } = settings;

  // Simplified calculation (real SS is much more complex)
  const monthlyIncome = avgAnnualIncome / 12;

  // Primary Insurance Amount (PIA) bend points (2024 approximation)
  let monthlyBenefit = 0;

  if (monthlyIncome <= 1115) {
    monthlyBenefit = monthlyIncome * 0.90;
  } else if (monthlyIncome <= 6721) {
    monthlyBenefit = 1115 * 0.90 + (monthlyIncome - 1115) * 0.32;
  } else {
    monthlyBenefit = 1115 * 0.90 + (6721 - 1115) * 0.32 + (monthlyIncome - 6721) * 0.15;
  }

  // Adjust for claiming age
  // Full Retirement Age (FRA) is 67 for those born 1960+
  // Early: reduce ~6.67%/year for first 3 years, 5%/year after (from 62-67)
  // Late: increase 8%/year up to age 70 (3 years of delayed credits)
  if (claimAge < 67) {
    const monthsEarly = (67 - claimAge) * 12;
    let reduction;
    if (monthsEarly <= 36) {
      // First 36 months: 5/9 of 1% per month = 6.67% per year
      reduction = monthsEarly * (5/9/100);
    } else {
      // After 36 months: 5/12 of 1% per month = 5% per year
      reduction = 36 * (5/9/100) + (monthsEarly - 36) * (5/12/100);
    }
    monthlyBenefit *= (1 - reduction);
  } else if (claimAge > 67) {
    // Delayed retirement credits: 8% per year, max to age 70 (3 years)
    const yearsLate = Math.min(claimAge - 67, 3);
    monthlyBenefit *= (1 + 0.08 * yearsLate);
  }

  // Apply user multiplier
  monthlyBenefit *= socialSecurityMultiplier;

  return monthlyBenefit * 12; // Annual benefit
};

/**
 * Generate automatic career events based on smart mode
 */
export const generateSmartCareerEvents = (startAge, retireAge, startIncome, settings = {}) => {
  const events = [];

  if (!settings.enableAutoPromotions) {
    return events;
  }

  let currentIncome = startIncome;
  let yearsSincePromotion = 0;

  for (let age = startAge; age < retireAge; age++) {
    yearsSincePromotion++;

    if (shouldAutoPromote(age, yearsSincePromotion, settings)) {
      const newIncome = calculatePromotionIncome(currentIncome, age, settings);

      events.push({
        id: `auto_promo_${age}`,
        type: 'promotion',
        age: age,
        newIncome: Math.round(newIncome),
        description: `Automatic promotion at age ${age}`,
        isAutoGenerated: true
      });

      currentIncome = newIncome;
      yearsSincePromotion = 0;
    }
  }

  return events;
};

/**
 * Validate and merge user settings with defaults
 */
export const getFinancialSettings = (userSettings = {}) => {
  return {
    ...DEFAULT_FINANCIAL_SETTINGS,
    ...userSettings
  };
};
